import React, { useCallback, useRef } from "react";
import t from "babel-plugin-yate/macro";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Field, Form } from "react-final-form";
import arrayMutators from "final-form-arrays";

import SectionHeader from "../components/SectionHeader";
import PageHeader from "../components/PageHeader";
import Button from "../components/Button";
import Icon from "../components/Icons";
import NoLocationPlaceholder from "../components/NoLocationPlaceholder";
import { Input } from "../components/Form/Input";
import Products from "../containers/Products";

import {
  composeFormValidators,
  isRequired,
  isNumber,
  hasMaxLength,
  isPhoneNumber,
  isEmail,
} from "../lib/validations";

import { addAlert } from "../store/app";
import { createLendingRequest } from "../store/request";

function RequestNew(props) {
  const {
    history,
    match: {
      params: { lendingType },
    },
  } = props;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const locationId = useSelector((state) => state.auth.locationId);
  const lendingTypes = useSelector((state) => state.auth.lendingTypes);
  const formRef = useRef(null);

  const isConsumerFinance = lendingTypes[lendingType].slug === "goods";

  const handleOnSubmit = useCallback(async () => {
    const formValues = formRef?.current?.getState()?.values;
    const res = await dispatch(
      createLendingRequest(formValues, {
        creditType: lendingTypes[lendingType].slug,
        locationId,
      }),
    );

    if (res.ok) {
      dispatch(
        addAlert({
          message: t`Successfully saved`,
          variant: "success",
        }),
      );

      history.push(`/${lendingTypes[lendingType].urlPath}`);
    }
  }, [dispatch, locationId, lendingTypes, lendingType, history]);

  if (locationId === "0") {
    return <NoLocationPlaceholder />;
  }

  return (
    <>
      <PageHeader
        title={lendingTypes[lendingType]?.name}
        breadcrumbsPages={[
          {
            title: lendingTypes[lendingType]?.name,
            route: `/${lendingTypes[lendingType]?.urlPath}`,
          },
          {
            title: t`New request`,
          },
        ]}
        accessType={user.access_type}
      />

      <SectionHeader
        title={t`Date client`}
        actions={
          <div className="flex items-center">
            <div className="ml-4">
              <Link to={`/${lendingTypes[lendingType].urlPath}`}>
                <Button
                  icon={true}
                  iconSrc={<Icon name="CloseButton" grey />}
                  color="white"
                  border={true}
                />
              </Link>
            </div>
          </div>
        }
      />

      <div className="">
        <div className="flex flex-wrap">
          <div className="w-full">
            <Form
              keepDirtyOnReinitialize={true}
              onSubmit={handleOnSubmit}
              mutators={arrayMutators}
              initialValues={{
                order_data: isConsumerFinance
                  ? {
                      products: [
                        {
                          category: null,
                          name: null,
                          sku: null,
                          quantity: null,
                          price: null,
                        },
                      ],
                    }
                  : {},
              }}
              render={({ form }) => {
                formRef.current = form;
                const { push } = form.mutators;

                return (
                  <>
                    <div className="px-4 border rounded flex-1 bg-white">
                      <div className="my-6 text-left">
                        <div className="md:flex md:flex-wrap mb-2">
                          <div className="w-full md:w-1/2 px-2 mb-2">
                            <Field
                              name="order_data.customer.lastname"
                              label={t`Last name`}
                              component={Input}
                              validate={isRequired()}
                            />
                          </div>

                          <div className="w-full md:w-1/2 px-2 mb-2">
                            <Field
                              name="order_data.customer.firstname"
                              label={t`First name`}
                              component={Input}
                              validate={isRequired()}
                            />
                          </div>
                        </div>

                        <div className="md:flex md:flex-wrap mb-5">
                          <div className="w-full md:w-1/2 px-2 mb-2">
                            <Field
                              name="order_data.customer.phone"
                              label={t`Mobile number`}
                              component={Input}
                              validate={composeFormValidators(
                                isRequired(),
                                isNumber(),
                                hasMaxLength(10),
                                isPhoneNumber(),
                              )}
                            />
                          </div>

                          <div className="w-full md:w-1/2 px-2 mb-2">
                            <Field
                              name="order_data.customer.email"
                              label={t`Email address`}
                              component={Input}
                              validate={composeFormValidators(
                                isRequired(),
                                isEmail(),
                              )}
                            />
                          </div>
                        </div>

                        {isConsumerFinance && (
                          <div className="w-full py-2">
                            <Products push={push} />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="my-8 mx-4 flex justify-end">
                      <div className="flex justify-end">
                        <div className="ml-4">
                          <Button
                            title={t`Continue`}
                            color="green"
                            onClick={form.submit}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                );
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default RequestNew;
